import {
  afterNextRender,
  Component,
  computed,
  inject,
  OnDestroy,
  viewChild,
  DOCUMENT,
} from '@angular/core';
import { MatSidenav, MatSidenavContainer } from '@angular/material/sidenav';
import { RouterOutlet } from '@angular/router';
import { Subscription } from 'rxjs';
import { DeviceService } from '@gains-com/ui-angular';
import { TopToolbarComponent } from './top-toolbar/top-toolbar.component';
import { LeftSidenavComponent } from './left-sidenav/left-sidenav.component';
import { ShellService } from './shell.service';
import { AsyncPipe } from '@angular/common';
import { RightSidenavComponent } from './right-sidenav/right-sidenav.component';
import { FooterComponent } from './footer/footer.component';
import { AppRouterService } from '../core/app-router.service';
import { BottomToolbarComponent } from './bottom-toolbar/bottom-toolbar.component';
import { PromoBannerComponent } from './promo-banner/promo-banner.component';
import { AppInstallBannerComponent } from './app-install-banner/app-install-banner.component';
import { LocationBannerComponent } from './location-banner/location-banner.component';

@Component({
  selector: 'app-shell',
  templateUrl: './shell.component.html',
  styleUrl: './shell.component.scss',
  host: {
    '[class.install-banner-visible]': 'bannerVisible()',
    '[class.location-banner-visible]': 'locationBannerVisible()'
  },
  imports: [
    MatSidenavContainer,
    MatSidenav,
    TopToolbarComponent,
    RouterOutlet,
    LeftSidenavComponent,
    AsyncPipe,
    RightSidenavComponent,
    FooterComponent,
    BottomToolbarComponent,
    PromoBannerComponent,
    AppInstallBannerComponent,
    LocationBannerComponent
  ]
})
export class ShellComponent implements OnDestroy {
  private readonly _appRouter = inject(AppRouterService);
  private readonly _document = inject(DOCUMENT);
  readonly shellService = inject(ShellService);
  readonly deviceService = inject(DeviceService);

  private readonly _installBanner = viewChild(AppInstallBannerComponent);
  readonly bannerVisible = computed(() => !!this._installBanner()?.shouldShowBanner());

  // Location banner outranks the install banner (one at a time) — the template
  // drops the install banner from the DOM while this is set.
  private readonly _locationBanner = viewChild(LocationBannerComponent);
  readonly locationBannerVisible = computed(() => !!this._locationBanner()?.shouldShowBanner());

  private _scrollToTopSubscription = Subscription.EMPTY;

  constructor() {
    afterNextRender(() => {
      // Because we size the sidenav content dynamically and set the inside to be scrollable, route changes will not reset
      // the scroll position when you're scrolled to the bottom. This is problematic for pages like terms-and-conditions
      // where starting at the top is pretty important. Hacking a solution in the meanwhile.
      // https://github.com/angular/components/issues/4280
      this._scrollToTopSubscription = this._appRouter.route$.subscribe(() => {
        const sidenavContent = this._document.querySelector(
          '.sidenav-content-container'
        );
        if (sidenavContent) {
          sidenavContent.scrollTop = 0;
        }
      });
    });
  }

  ngOnDestroy() {
    this._scrollToTopSubscription.unsubscribe();
  }
}
